import { useEffect, useRef, useMemo, memo } from 'react';
import * as d3 from 'd3';
import { CodeBracketIcon, BugAntIcon } from '@heroicons/react/24/outline';
import { useTasks } from '@/hooks/useTasks';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import {
    groupTasksBySwimlane,
    calculateSwimLaneHeight,
    getSwimlaneType,
    getLaneHexColor,
    type SwimLaneType
} from '@/utils/swimlaneUtils';
import type { Database } from '@/types/database.types';

type Task = Database['public']['Tables']['tasks']['Row'];
type TaskType = Database['public']['Enums']['task_type'];

/**
 * Timeline data structure for D3 rendering
 */
interface TimelineTask {
    id: number;
    name: string;
    start: Date;
    end: Date;
    type: TaskType;
    status: Task['status'];
    swimLane: SwimLaneType;
}

/**
 * Get task type color per design-system.md
 */
const getTaskColor = (type: TaskType): string => {
    const colorMap: Record<TaskType, string> = {
        'design': '#9333EA',        // purple-600
        'development': '#2563EB',   // blue-600
        'integration': '#4F46E5',   // indigo-600
        'code_review': '#0891B2',   // cyan-600
        'qa': '#059669'             // emerald-600
    };
    return colorMap[type] || '#6B7280'; // gray-500 fallback
};

/**
 * Get task type label for display
 */
const getTaskTypeLabel = (type: TaskType): string => {
    return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
};

interface ProjectTimelineSwimLanesProps {
    projectId: number;
}

/**
 * Compute row assignments for all tasks in a lane using greedy first-fit packing.
 * Called once per lane (O(n²) total) rather than once per task (O(n³)).
 * Returns Map<task_id, row_index>.
 */
const computeRowAssignments = (tasks: Task[]): Map<number, number> => {
    const rowMap = new Map<number, number>();
    const rows: Task[][] = [[]];

    tasks.forEach(t => {
        const tStart = new Date(t.start_date).getTime();
        const tEnd = new Date(t.end_date).getTime();
        let placed = false;

        for (let r = 0; r < rows.length; r++) {
            const row = rows[r]!;
            const canFit = row.every(existing => {
                const eStart = new Date(existing.start_date).getTime();
                const eEnd = new Date(existing.end_date).getTime();
                return tEnd <= eStart || tStart >= eEnd;
            });
            if (canFit) {
                row.push(t);
                rowMap.set(t.id, r);
                placed = true;
                break;
            }
        }

        if (!placed) {
            rows.push([t]);
            rowMap.set(t.id, rows.length - 1);
        }
    });

    return rowMap;
};

/**
 * ProjectTimelineSwimLanes - Renders project tasks in swim lanes (Developer/QA) using D3.js
 * Memoized to prevent unnecessary re-renders
 */
const ProjectTimelineSwimLanesComponent = ({ projectId }: ProjectTimelineSwimLanesProps) => {
    const { data: tasks, isLoading, isError } = useTasks(projectId);
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Group tasks by swim lane and transform to timeline data.
    // Filters out tasks with null, invalid, or inverted dates before rendering.
    const { developerTasks, qaTasks, timelineData } = useMemo(() => {
        if (!tasks || tasks.length === 0) {
            return { developerTasks: [], qaTasks: [], timelineData: [] };
        }

        const validTasks = tasks.filter(t => {
            if (t.start_date == null || t.end_date == null) return false;
            const s = new Date(t.start_date).getTime();
            const e = new Date(t.end_date).getTime();
            return !isNaN(s) && !isNaN(e) && s <= e;
        });

        const grouped = groupTasksBySwimlane(validTasks);

        const timeline: TimelineTask[] = validTasks.map(task => ({
            id: task.id,
            name: task.name,
            start: new Date(task.start_date),
            end: new Date(task.end_date),
            type: task.type,
            status: task.status,
            swimLane: getSwimlaneType(task.type)
        }));

        return {
            developerTasks: grouped.developer,
            qaTasks: grouped.qa,
            timelineData: timeline
        };
    }, [tasks]);

    useEffect(() => {
        if (!svgRef.current || !containerRef.current || !timelineData || timelineData.length === 0) return;

        const svg = d3.select(svgRef.current);
        const container = containerRef.current;

        // Clear previous render
        svg.selectAll('*').remove();

        // Dimensions
        const margin = { top: 60, right: 40, bottom: 60, left: 220 };
        const containerWidth = container.clientWidth;
        const width = Math.max(1200, containerWidth) - margin.left - margin.right;
        const barHeight = 32;
        const barPadding = 8;

        // Calculate swim lane heights (uses same greedy packing as computeRowAssignments)
        const devLaneHeight = calculateSwimLaneHeight(developerTasks, barHeight, barPadding);
        const qaLaneHeight = calculateSwimLaneHeight(qaTasks, barHeight, barPadding);
        const totalHeight = devLaneHeight + qaLaneHeight + margin.top + margin.bottom + 40;

        svg
            .attr('width', width + margin.left + margin.right)
            .attr('height', totalHeight)
            .attr('class', 'timeline-svg');

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Compute date range — guard against all-invalid (filtered above but defensive)
        const minDate = d3.min(timelineData, d => d.start);
        const maxDate = d3.max(timelineData, d => d.end);
        if (!minDate || !maxDate || isNaN(minDate.getTime()) || isNaN(maxDate.getTime())) return;

        // Guard zero-duration: use 1-day padding when all tasks share the same date
        const dateRange = maxDate.getTime() - minDate.getTime();
        const datePadding = dateRange === 0 ? 86400000 : dateRange * 0.05;

        // Create time scale
        const xScale = d3.scaleTime()
            .domain([
                new Date(minDate.getTime() - datePadding),
                new Date(maxDate.getTime() + datePadding)
            ])
            .range([0, width])
            .nice();

        // Create swim lane groups
        const devLaneGroup = g.append('g')
            .attr('class', 'swim-lane swim-lane-developer')
            .attr('transform', 'translate(0,0)');

        const qaLaneGroup = g.append('g')
            .attr('class', 'swim-lane swim-lane-qa')
            .attr('transform', `translate(0,${devLaneHeight + 20})`);

        // Swim lane backgrounds — solid fills per AC #10, no unspecified stroke decorations
        devLaneGroup.append('rect')
            .attr('x', -margin.left)
            .attr('y', -20)
            .attr('width', width + margin.left + margin.right)
            .attr('height', devLaneHeight)
            .attr('fill', getLaneHexColor('developer'))
            .attr('opacity', 0.9);

        qaLaneGroup.append('rect')
            .attr('x', -margin.left)
            .attr('y', -20)
            .attr('width', width + margin.left + margin.right)
            .attr('height', qaLaneHeight)
            .attr('fill', getLaneHexColor('qa'))
            .attr('opacity', 0.9);

        // Lane separator — 2px solid gray-200 per AC #11
        g.append('line')
            .attr('x1', -margin.left)
            .attr('x2', width + margin.right)
            .attr('y1', devLaneHeight + 10)
            .attr('y2', devLaneHeight + 10)
            .attr('stroke', '#E5E7EB') // gray-200
            .attr('stroke-width', 2);

        // Swim lane labels — dynamic background widths prevent overflow with large task counts
        const labelY = -25;

        const devLabel = `Developer Tasks (${developerTasks.length})`;
        const devLabelWidth = devLabel.length * 8 + 20;
        devLaneGroup.append('rect')
            .attr('x', -margin.left + 5)
            .attr('y', labelY - 15)
            .attr('width', devLabelWidth)
            .attr('height', 20)
            .attr('fill', '#DBEAFE')
            .attr('stroke', '#60A5FA')
            .attr('stroke-width', 1)
            .attr('rx', 4);

        devLaneGroup.append('text')
            .attr('x', -margin.left + 10)
            .attr('y', labelY)
            .attr('class', 'text-sm font-semibold')
            .attr('fill', '#1E40AF')
            .text(devLabel);

        const qaLabel = `QA Tasks (${qaTasks.length})`;
        const qaLabelWidth = qaLabel.length * 8 + 20;
        qaLaneGroup.append('rect')
            .attr('x', -margin.left + 5)
            .attr('y', labelY - 15)
            .attr('width', qaLabelWidth)
            .attr('height', 20)
            .attr('fill', '#FCE7F3')
            .attr('stroke', '#F472B6')
            .attr('stroke-width', 1)
            .attr('rx', 4);

        qaLaneGroup.append('text')
            .attr('x', -margin.left + 10)
            .attr('y', labelY)
            .attr('class', 'text-sm font-semibold')
            .attr('fill', '#9F1239')
            .text(qaLabel);

        // Compute row assignments once per lane — O(n²) total instead of O(n³)
        const devRowMap = computeRowAssignments(developerTasks);
        const qaRowMap = computeRowAssignments(qaTasks);

        // Grid lines
        const gridGroup = g.append('g').attr('class', 'grid');
        const xTicks = xScale.ticks(d3.timeWeek.every(1)!);
        gridGroup.selectAll('.grid-line')
            .data(xTicks)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', d => xScale(d) ?? 0)
            .attr('x2', d => xScale(d) ?? 0)
            .attr('y1', -20)
            .attr('y2', devLaneHeight + qaLaneHeight + 20)
            .attr('stroke', '#E5E7EB')
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '2,2');

        // Today indicator
        const today = new Date();
        const [domainStart, domainEnd] = xScale.domain();
        if (domainStart && domainEnd && today >= domainStart && today <= domainEnd) {
            const todayX = xScale(today) ?? 0;
            g.append('line')
                .attr('class', 'today-line')
                .attr('x1', todayX)
                .attr('x2', todayX)
                .attr('y1', -30)
                .attr('y2', devLaneHeight + qaLaneHeight + 20)
                .attr('stroke', '#DC2626')
                .attr('stroke-width', 2)
                .attr('stroke-dasharray', '4,4');

            g.append('text')
                .attr('x', todayX)
                .attr('y', -35)
                .attr('text-anchor', 'middle')
                .attr('class', 'text-xs font-medium')
                .attr('fill', '#DC2626')
                .text('Today');
        }

        // X-axis
        const xAxis = d3.axisBottom(xScale)
            .ticks(d3.timeWeek.every(1))
            .tickFormat(d3.timeFormat('%b %d') as (d: Date | d3.NumberValue) => string);

        g.append('g')
            .attr('class', 'x-axis')
            .attr('transform', `translate(0,${devLaneHeight + qaLaneHeight + 20})`)
            .call(xAxis)
            .selectAll('text')
            .attr('class', 'text-sm')
            .attr('fill', '#374151');

        g.selectAll('.x-axis path, .x-axis line')
            .attr('stroke', '#D1D5DB');

        // Render developer tasks using pre-computed row assignments
        const devTasksData = timelineData.filter(t => t.swimLane === 'developer');
        devTasksData.forEach(task => {
            const row = devRowMap.get(task.id) ?? 0;
            renderTaskBar(devLaneGroup, task, xScale, row * (barHeight + barPadding), barHeight);
        });

        // Render QA tasks using pre-computed row assignments
        const qaTasksData = timelineData.filter(t => t.swimLane === 'qa');
        qaTasksData.forEach(task => {
            const row = qaRowMap.get(task.id) ?? 0;
            renderTaskBar(qaLaneGroup, task, xScale, row * (barHeight + barPadding), barHeight);
        });

        // Per-lane empty state messages per AC #13
        if (developerTasks.length === 0) {
            devLaneGroup.append('text')
                .attr('x', width / 2)
                .attr('y', devLaneHeight / 2)
                .attr('text-anchor', 'middle')
                .attr('class', 'text-sm')
                .attr('fill', '#9CA3AF')
                .text('No Developer tasks');
        }

        if (qaTasks.length === 0) {
            qaLaneGroup.append('text')
                .attr('x', width / 2)
                .attr('y', qaLaneHeight / 2)
                .attr('text-anchor', 'middle')
                .attr('class', 'text-sm')
                .attr('fill', '#9CA3AF')
                .text('No QA tasks');
        }

        // Helper function to render a task bar
        function renderTaskBar(
            parent: d3.Selection<SVGGElement, unknown, null, undefined>,
            task: TimelineTask,
            xScale: d3.ScaleTime<number, number>,
            yPos: number,
            height: number
        ) {
            const taskGroup = parent.append('g')
                .attr('class', 'task-group')
                .attr('transform', `translate(0,${yPos})`);

            taskGroup.append('rect')
                .attr('class', 'task-bar')
                .attr('x', xScale(task.start))
                .attr('y', 0)
                .attr('width', Math.max(4, xScale(task.end) - xScale(task.start)))
                .attr('height', height)
                .attr('rx', 4)
                .attr('fill', getTaskColor(task.type))
                .attr('opacity', task.status === 'completed' ? 0.6 : 1)
                .style('cursor', 'pointer')
                .on('mouseenter', function () {
                    d3.select(this).transition().duration(150)
                        .attr('opacity', task.status === 'completed' ? 0.7 : 0.8);
                })
                .on('mouseleave', function () {
                    d3.select(this).transition().duration(150)
                        .attr('opacity', task.status === 'completed' ? 0.6 : 1);
                })
                .append('title')
                .text(() => {
                    const start = task.start.toLocaleDateString();
                    const end = task.end.toLocaleDateString();
                    return `${task.name}\nType: ${getTaskTypeLabel(task.type)}\nDates: ${start} - ${end}\nStatus: ${task.status.replace(/_/g, ' ')}`;
                });

            const barWidth = xScale(task.end) - xScale(task.start);
            if (barWidth > 60) {
                taskGroup.append('text')
                    .attr('x', xScale(task.start) + 8)
                    .attr('y', height / 2)
                    .attr('dy', '0.35em')
                    .attr('class', 'text-xs font-medium')
                    .attr('fill', 'white')
                    .text(task.name.length > 20 ? task.name.substring(0, 20) + '...' : task.name)
                    .style('pointer-events', 'none');
            }

            taskGroup.append('circle')
                .attr('cx', xScale(task.start) + 6)
                .attr('cy', height / 2)
                .attr('r', 3)
                .attr('fill', 'white')
                .attr('opacity', 0.9)
                .style('pointer-events', 'none');
        }

    }, [timelineData, developerTasks, qaTasks]);

    // Loading state
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <Spinner size="lg" />
                    <p className="mt-4 text-gray-600">Loading timeline...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <Alert variant="error">
                Failed to load timeline tasks. Please try again.
            </Alert>
        );
    }

    // Global empty state (no tasks at all)
    if (!tasks || tasks.length === 0) {
        return (
            <EmptyState
                title="No tasks yet"
                description="Create tasks for this project to see them visualized on the timeline."
            />
        );
    }

    return (
        <div className="timeline-container">
            {/* Swim lane header badges — AC #8/#9 */}
            <div className="bg-white rounded-t-lg shadow-sm p-4 border-b border-gray-200">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-md">
                        <CodeBracketIcon className="w-4 h-4" aria-hidden="true" />
                        <span className="text-sm font-semibold">
                            Developer Tasks ({developerTasks.length})
                        </span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-pink-50 border border-pink-200 text-pink-800 rounded-md">
                        <BugAntIcon className="w-4 h-4" aria-hidden="true" />
                        <span className="text-sm font-semibold">
                            QA Tasks ({qaTasks.length})
                        </span>
                    </div>
                </div>
            </div>

            <div
                ref={containerRef}
                className="overflow-x-auto bg-white rounded-b-lg shadow"
                role="region"
                aria-label="Project timeline with swim lanes"
            >
                <svg
                    ref={svgRef}
                    className="timeline-svg"
                    role="img"
                    aria-label={`Timeline showing ${tasks.length} tasks in swim lanes`}
                />
            </div>

            {/* Legend */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Task Types</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                    {(['design', 'development', 'integration', 'code_review', 'qa'] as TaskType[]).map(type => (
                        <div key={type} className="flex items-center gap-2">
                            <div
                                className="w-4 h-4 rounded"
                                style={{ backgroundColor: getTaskColor(type) }}
                                aria-hidden="true"
                            />
                            <span className="text-gray-700">{getTaskTypeLabel(type)}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Export memoized component for performance
export const ProjectTimelineSwimLanes = memo(ProjectTimelineSwimLanesComponent);
